import { useCallback, useEffect, useState } from 'react'
import * as adminApi from '../../adminApi'
import { ApiError } from '../../api'
import type { AdminInvite, RoomType } from '../../types'
import { ConsoleManager } from './ConsoleManager'

interface ConsoleShellProps {
  onSignOut: () => void
  /** Called when a request 401s, so the console can drop back to the prompt. */
  onSessionLost: () => void
}

/**
 * The console's frame: the header and the guest list under it.
 *
 * The invites and the rooms are loaded here rather than further down, because
 * they overlap - the guest list shows which room each invite holds, and the
 * totals count both. Loading them together also means nothing further down has
 * to show a spinner over data already in hand.
 */
export function ConsoleShell({ onSignOut, onSessionLost }: ConsoleShellProps) {
  const [invites, setInvites] = useState<AdminInvite[] | null>(null)
  const [roomTypes, setRoomTypes] = useState<RoomType[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Any 401 means the admin session went away, so hand back to the prompt
  // rather than showing an error the user cannot act on.
  const handle = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return 'Your session expired.'
      }
      return err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
    },
    [onSessionLost],
  )

  // Both are refreshed after any change, because the changes cross over: a
  // guest who withdraws gives up the room their invite was holding.
  const reload = useCallback(async () => {
    try {
      const [nextInvites, nextRooms] = await Promise.all([
        adminApi.listInvites(),
        adminApi.listRooms(),
      ])
      setInvites(nextInvites)
      setRoomTypes(nextRooms)
      setLoadError(null)
    } catch (err) {
      setLoadError(handle(err))
    }
  }, [handle])

  useEffect(() => {
    void reload()
  }, [reload])

  const ready = invites !== null && roomTypes !== null

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Tom &amp; Jess</h1>
          <p className="text-ink-soft text-sm">Admin console</p>
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="text-ink-soft hover:text-claret text-xs tracking-widest uppercase transition-colors"
        >
          Sign out
        </button>
      </header>

      {loadError && (
        <p role="alert" className="text-claret mt-8 text-sm">
          {loadError}
        </p>
      )}

      {!ready && !loadError && <p className="text-ink-soft mt-8 text-sm">Loading…</p>}

      {ready && (
        <div className="mt-8">
          <ConsoleManager
            invites={invites}
            roomTypes={roomTypes}
            onChanged={() => void reload()}
            onSessionLost={onSessionLost}
          />
        </div>
      )}
    </main>
  )
}
